import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  Alert
} from '@mui/material';
import AppDialog, { AppDialogTitle, AppDialogContent, AppDialogActions } from '../layout/AppDialog';
import { resendOtp } from '../../services/api';

const ResendOtpModal = ({ open, onClose, order, onOtpResent }) => {
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info'); // 'info', 'success', 'error'

  useEffect(() => {
    if (open && order) {
      setMessage(`Resend OTP for order ${order.order_no ? `#${order.order_no}` : `waybill #${order.waybill_no}`}?`);
      setMessageType('info');
    }
  }, [open, order]);

  const handleResendOtp = async () => {
    if (!order || isResending) return;

    setIsResending(true);
    setMessage('Sending new OTP...');
    setMessageType('info');

    try {
      const response = await resendOtp(order.id);
      
      if (response.data?.warning) {
        setMessage(`Warning: ${response.data.message}`);
        setMessageType('error');
      } else {
        setMessage('New OTP sent successfully!');
        setMessageType('success');
        
        // Call the callback to refresh parent component
        if (onOtpResent) {
          onOtpResent();
        }
        
        // Close modal after 2 seconds on success
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error sending OTP';
      setMessage(`Error: ${errorMessage}`);
      setMessageType('error');
    } finally {
      setIsResending(false);
    }
  };

  const handleClose = () => {
    if (!isResending) {
      onClose();
    }
  };

  if (!order) return null;

  return (
    <AppDialog open={open} onClose={handleClose}>
      <AppDialogTitle onClose={handleClose} subtitle="Send a new OTP to the customer via SMS">
        Resend OTP
      </AppDialogTitle>
      
      <AppDialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Customer: <strong>{order.customer_name}</strong>
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            Phone: <strong>{order.customer_phone}</strong>
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            Order: <strong>{order.order_no ? `#${order.order_no}` : `Waybill #${order.waybill_no}`}</strong>
          </Typography>

          {message && (
            <Alert severity={messageType} sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            This will generate a new OTP code and send it to the customer via SMS. 
            The previous OTP will become invalid.
          </Typography>
        </Box>
      </AppDialogContent>

      <AppDialogActions>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          color="inherit" 
          disabled={isResending}
        >
          Cancel
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          onClick={handleResendOtp}
          disabled={isResending}
          startIcon={isResending ? <CircularProgress size={20} /> : null}
        >
          {isResending ? 'Sending OTP...' : 'Resend OTP'}
        </Button>
      </AppDialogActions>
    </AppDialog>
  );
};

export default ResendOtpModal;
